import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { get, uniq, uniqBy, map, flatten } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsAPI } from 'client/data/api/api-client';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { getQuery } from 'client/utils/location';
import { handleConfigurationRules } from 'site-modules/shared/utils/build-price/build-price';
import { GRAPHQL_FRAGMENTS } from 'client/data/constants/graphql-fragments';
import { formatPhotosURL } from 'client/data/utils/format-urls';
import { PageModel } from './page';
import {
  buildMakeModelDefaultYear as vehicleBuildMakeModelDefaultYear,
  VehicleModel,
  FeatureSpecsPaths,
} from './vehicle-v2';
import { VisitorModel } from './visitor';

const DEFAULT_OPTION_ID = 'default';

const ModelYear = PropTypes.shape({
  makeName: PropTypes.string,
  makeSlug: PropTypes.string,
  modelName: PropTypes.string,
  modelSlug: PropTypes.string,
  year: PropTypes.number,
  id: PropTypes.string,
  submodels: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string })),
});

const Submodel = PropTypes.shape({
  name: PropTypes.string,
  slug: PropTypes.string,
  baseMSRP: PropTypes.number,
});

const YearSubmodels = PropTypes.shape({
  id: PropTypes.string,
  year: PropTypes.number,
  submodels: PropTypes.arrayOf(Submodel),
});

const Style = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.shape({ baseMSRP: PropTypes.number }),
});

const StyleFilter = PropTypes.objectOf(PropTypes.string);

const TmvPrice = PropTypes.shape({
  baseMSRP: PropTypes.number,
  baseInvoice: PropTypes.number,
  tmv: PropTypes.number,
});

const Tmv = PropTypes.shape({
  destinationCharge: PropTypes.number,
  incentivesAndRebates: PropTypes.number,
  adjustments: PropTypes.shape({
    colorAdjustment: TmvPrice,
    regionalAdjustment: TmvPrice,
  }),
  gasGuzzlerTax: PropTypes.number,
  nationalBasePrice: TmvPrice,
  optionTmvPrices: PropTypes.arrayOf(
    PropTypes.shape({
      optionId: PropTypes.string,
      price: TmvPrice,
    })
  ),
  totalWithOptions: TmvPrice,
});

const OptionPriceAdjustments = PropTypes.arrayOf(
  PropTypes.shape({
    price: PropTypes.shape({ baseMSRP: PropTypes.number, baseInvoice: PropTypes.number }),
  })
);

const OptionsConfig = PropTypes.shape({
  styleName: PropTypes.string,
  optionPriceAdjustments: OptionPriceAdjustments,
  included: PropTypes.arrayOf(PropTypes.string),
  allOptions: PropTypes.arrayOf(PropTypes.string),
});

const ConditionOption = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  finalOptionMSRP: PropTypes.number,
});

const Option = PropTypes.oneOfType([
  PropTypes.shape({
    id: PropTypes.string,
    oemName: PropTypes.string,
    price: PropTypes.shape({ baseMSRP: PropTypes.number, oemDiscountMsrp: PropTypes.number }),
    description: PropTypes.string,
    category: PropTypes.string,
  }),
  PropTypes.shape({
    id: PropTypes.string,
    oemName: PropTypes.string,
    price: PropTypes.shape({ baseMSRP: PropTypes.number, oemDiscountMsrp: PropTypes.number }),
    colorType: PropTypes.string,
    primaryRGB: PropTypes.arrayOf(PropTypes.number),
    category: PropTypes.string,
  }),
]);

const BuildPriceFilter = PropTypes.shape({
  step: PropTypes.number,
  styleId: PropTypes.string,
  optionIds: PropTypes.string,
  urlOptionIds: PropTypes.string, // optionIds without included options
  defaultValue: PropTypes.string,
  styleFilter: StyleFilter,
  defaultOptionIds: PropTypes.string,
});

const ColorPhoto = PropTypes.shape({
  title: PropTypes.string,
  color: PropTypes.string,
  src: PropTypes.string,
});

export const BuildPriceEntities = {
  ModelYear,
  Submodel,
  YearSubmodels,
  Style,
  BuildPriceFilter,
  StyleFilter,
  Tmv,
  OptionsConfig,
  Option,
  ConditionOption,
  OptionPriceAdjustments,
  ColorPhoto,
};

const getOptionIdsForPath = optionIds =>
  optionIds
    ? optionIds
        .split(',')
        .sort((a, b) => a - b)
        .toString()
    : DEFAULT_OPTION_ID;

function buildStylesFeaturesPath(styleIdList) {
  return `styleIds["${styleIdList}"].features`;
}

function buildModelYearsPath({ make, model }) {
  return `makes["${make}"].models["${model}"].modelYears`;
}

function buildMakeModelDefaultYear({ make, model }) {
  return `makes["${make}"].models["${model}"].defaultYear`;
}

function buildStyleOptionsPath(styleId) {
  return `styleIds["${styleId}"].configurations.options`;
}

function buildConfigurationWithOptionsPath(styleId, optionIds) {
  return `styleIds["${styleId}"].configurations.withOptions["${getOptionIdsForPath(optionIds)}"]`;
}

function buildConfigurationTmvWithOptionsPath(styleId, optionIds) {
  return `styleIds["${styleId}"].configurations.tmv.withOptions["${getOptionIdsForPath(optionIds)}"]`;
}

export function buildConfigurationTmvSimplifiedWithOptionsPath(styleId, dealerZip, colorId, optionIds) {
  return `styleIds["${styleId}"].dealerZip["${dealerZip}"].colorId["${colorId}"].configurations.tmv.withOptions["${getOptionIdsForPath(
    optionIds
  )}"]`;
}

function buildStylesListPath({ make, model, submodel, year }) {
  return `makes["${make}"].models["${model}"].submodels["${submodel}"].years["${year}"].styles`;
}

function buildYearsSubmodelsPath({ make, model }) {
  return `makes["${make}"].models["${model}"].yearsSubmodels`;
}

function buildColorPhotosPath({ make, model, submodel, year }) {
  return `makes["${make}"].models["${model}"].submodels["${submodel}"].years["${year}"].colorPhotos`;
}

function getBuildPriceFilterPath() {
  return 'buildPriceFilter';
}

export const BuildPricePaths = {
  buildStylesFeaturesPath,
  buildModelYearsPath,
  buildMakeModelDefaultYear,
  buildStyleOptionsPath,
  buildConfigurationWithOptionsPath,
  buildConfigurationTmvWithOptionsPath,
  buildStylesListPath,
  buildYearsSubmodelsPath,
  buildColorPhotosPath,
  getBuildPriceFilterPath,
};

export const BuildPriceModel = createModelSegment('buildPrice', [
  /**
   * @see getBuildPriceFilterPath
   */
  {
    path: 'buildPriceFilter',
    resolve() {
      return Promise.resolve(null);
    },
    update(newValue, match, context) {
      return context
        .resolveValue(BuildPricePaths.getBuildPriceFilterPath())
        .then(buildPriceFilter => ({ step: get(buildPriceFilter, 'step'), ...newValue }));
    },
  },
  /**
   * @see buildModelYearsPath()
   * @returns {Array<ModelYear>}
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].modelYears',
    resolve({ makeSlug, modelSlug }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!) {
              newModelYears: modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, publicationState: NEW) {
                makeName
                makeSlug
                modelName
                modelSlug
                year
                id
                submodels(page: { pageNum: 1, pageSize: 1 }, sortBy: { shareBySales: DESC }) {
                  slug
                }
              }

              newUsedModelYears: modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, publicationState: NEW_USED) {
                makeName
                makeSlug
                modelName
                modelSlug
                year
                id
                submodels(page: { pageNum: 1, pageSize: 1 }, sortBy: { shareBySales: DESC }) {
                  slug
                }
              }
            }
          `,
          {
            makeSlug,
            modelSlug,
          }
        )
        .then(({ newModelYears, newUsedModelYears }) => {
          const modelYears = newModelYears && newModelYears.concat(newUsedModelYears);

          if (!get(modelYears, 'length')) {
            return null;
          }

          return uniqBy(modelYears, 'year').sort(({ year: yearA }, { year: yearB }) => yearB - yearA);
        });
    },
  },
  /**
   * Returns defaultYear from query string. Otherwise uses API
   * Example: https://qa-21-www.edmunds.com/gateway/api/coreresearch/v1/defaultyear/honda/civic
   * @see buildMakeModelDefaultYear
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].defaultYear',
    async resolve({ makeSlug, modelSlug }, context) {
      const location = await context.resolveValue('location', PageModel);

      const { defaultYear } = getQuery(location);
      if (defaultYear) {
        return Promise.resolve(parseInt(defaultYear, 10));
      }

      return context.resolveValue(vehicleBuildMakeModelDefaultYear({ make: makeSlug, model: modelSlug }), VehicleModel);
    },
  },
  /**
   * Example (for each style): https://qa-21-www.edmunds.com/api/vehicle/v5/styles/401773859/features-specs
   * @see buildStylesFeaturesPath
   */
  {
    path: 'styleIds["{styleIds}"].features',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds
          .split(',')
          .map(styleId => context.resolveValue(FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel))
      );
    },
  },
  /**
   * Returns NEW_USED years and their submodels by MM
   * @see buildYearsSubmodelsPath
   * @returns {YearSubmodels}
   */
  {
    path: 'makes["{make}"].models["{model}"].yearsSubmodels',
    resolve({ make, model }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!) {
              newModelYears: modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, publicationState: NEW) {
                id
                year
                submodels {
                  id
                  name
                  slug
                  styleList {
                    styleAggregations {
                      priceRange {
                        min
                      }
                    }
                  }
                }
              }
              newUsedModelYears: modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, publicationState: NEW_USED) {
                id
                year
                submodels {
                  id
                  name
                  slug
                  styleList {
                    styleAggregations {
                      priceRange {
                        min
                      }
                    }
                  }
                }
              }
            }
          `,
          {
            makeSlug: make,
            modelSlug: model,
          }
        )
        .then(({ newModelYears, newUsedModelYears }) =>
          uniqBy(newModelYears?.concat(newUsedModelYears), 'id')
            .map(modelYear => ({
              ...modelYear,
              submodels: modelYear.submodels
                .map(submodel => ({
                  ...submodel,
                  baseMSRP: get(submodel, 'styleList.styleAggregations.priceRange.min'),
                }))
                .sort((a, b) => a.baseMSRP - b.baseMSRP),
            }))
            .sort((a, b) => b.year - a.year)
        );
    },
  },
  /**
   * Returns styles by MMSY sorted by price
   * @see buildStylesListPath
   */
  {
    path: 'makes["{make}"].models["{model}"].submodels["{submodel}"].years["{year}"].styles',
    resolve({ make, model, submodel, year }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!, $submodelSlug: String!, $year: Int!) {
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                submodels(filter: { slug: $submodelSlug }) {
                  styleList {
                    styles {
                      id
                      name
                      trimName
                      price {
                        baseMSRP
                      }
                    }
                  }
                }
              }
            }
          `,
          {
            makeSlug: make,
            modelSlug: model,
            submodelSlug: submodel,
            year: parseInt(year, 10),
          }
        )
        .then(result =>
          get(result, 'modelYears[0].submodels[0].styleList.styles', [])
            .slice()
            .sort((a, b) => get(a, 'price.baseMSRP', 0) - get(b, 'price.baseMSRP', 0))
        );
    },
  },
  /**
   * Returns style options list
   * @see buildStyleOptionsPath
   * @returns {Array<Option>}
   */
  {
    path: 'styleIds["{styleId}"].configurations.options',
    resolve({ styleId }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            ${GRAPHQL_FRAGMENTS.optionFields}
            query ($styleId: String!) {
              style(styleId: $styleId) {
                options {
                  colors {
                    ...optionFields
                  }
                  packages {
                    ...optionFields
                  }
                  mechanicals {
                    ...optionFields
                  }
                  interiors {
                    ...optionFields
                  }
                  exteriors {
                    ...optionFields
                  }
                  additionalFees {
                    ...optionFields
                  }
                  safetyFeatures {
                    ...optionFields
                  }
                }
              }
            }
          `,
          { styleId }
        )
        .then(result =>
          flatten(
            map(get(result, 'style.options', {}), (options, category) =>
              options.map(option => ({ ...option, category }))
            )
          )
        );
    },
  },
  /**
   * Returns configuration with selected options or default configurations if no options selected
   * Example (default): https://qa-21-www.edmunds.com/api/vehicle/v3/styles/401822600/configuration?default=true
   * Example (with options): https://qa-21-www.edmunds.com/api/vehicle/v3/styles/401822600/configuration?currentOptions=401822811,401822886,401823746,401823750,401824130&addOption=401824145
   * @see buildConfigurationWithOptionsPath
   * @returns {OptionsConfigCore}
   */
  {
    path: 'styleIds["{styleId}"].configurations.withOptions["{optionIds}"]',
    async resolve({ styleId, optionIds }, context) {
      let url;

      if (optionIds === DEFAULT_OPTION_ID) {
        url = `/vehicle/v3/styles/${styleId}/configuration?default=true`;
      } else {
        const optionIdsArray = uniq(optionIds.split(','));

        url = `/vehicle/v3/styles/${styleId}/configuration?currentOptions=${optionIdsArray.splice(
          0,
          optionIdsArray.length - 1
        )}&addOption=${optionIdsArray.splice(-1)}`;
      }

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(({ configuration, style }) => {
          const configurationWithoutRules =
            configuration && handleConfigurationRules({ styleName: get(style, 'name'), ...configuration });
          const defaultOptionIds = get(configurationWithoutRules, 'allOptions', '').toString();

          if ((optionIds === DEFAULT_OPTION_ID || optionIds !== defaultOptionIds) && !!defaultOptionIds) {
            return context
              .updateValue(buildConfigurationWithOptionsPath(styleId, defaultOptionIds), configurationWithoutRules)
              .then(() => ({ $ref: `#/styleIds/${styleId}/configurations/withOptions/${defaultOptionIds}` }));
          }

          return configurationWithoutRules;
        });
    },
  },
  /**
   * Returns TMV for configuration with options
   * @see buildConfigurationTmvWithOptionsPath
   */
  {
    path: 'styleIds["{styleId}"].configurations.tmv.withOptions["{optionIds}"]',
    async resolve({ styleId, optionIds }, context) {
      const { zipCode } = await context.resolveValue('location', VisitorModel);

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($styleId: String!, $input: NewTmvInput!) {
              style(styleId: $styleId) {
                newTmv(input: $input) {
                  adjustments {
                    colorAdjustment {
                      baseMSRP
                      baseInvoice
                      tmv
                    }
                    regionalAdjustment {
                      baseMSRP
                      baseInvoice
                      tmv
                    }
                  }
                  destinationCharge
                  gasGuzzlerTax
                  incentivesAndRebates
                  nationalBasePrice {
                    baseMSRP
                    baseInvoice
                    tmv
                  }
                  optionTmvPrices {
                    optionId
                    price {
                      baseMSRP
                      baseInvoice
                      tmv
                    }
                  }
                  totalWithOptions {
                    baseMSRP
                    baseInvoice
                    tmv
                  }
                }
              }
            }
          `,
          {
            styleId,
            input: {
              zipcode: zipCode?.toString(),
              optionSelection:
                optionIds === DEFAULT_OPTION_ID ? { typical: true } : { optionIds: optionIds.split(',') },
            },
          }
        )
        .then(response => get(response, 'style.newTmv'))
        .catch(() => null);
    },
  },
  /**
   * Returns the total TMV for a configuration with options
   * @see buildConfigurationTmvSimplifiedWithOptionsPath
   */
  {
    path: 'styleIds["{styleId}"].dealerZip["{dealerZip}"].colorId["{colorId}"].configurations.tmv.withOptions["{optionIds}"]',
    async resolve({ styleId, dealerZip, colorId, optionIds }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($styleId: String!, $input: NewTmvInput!) {
              style(styleId: $styleId) {
                newTmv(input: $input) {
                  totalWithOptions {
                    tmv
                  }
                }
              }
            }
          `,
          {
            styleId,
            input: {
              zipcode: dealerZip?.toString(),
              ...(colorId !== undefined && colorId !== null ? { colorId: colorId.toString() } : {}),
              optionSelection:
                optionIds === DEFAULT_OPTION_ID ? { typical: true } : { optionIds: optionIds.split(',') },
            },
          }
        )
        .then(response => get(response, 'style.newTmv.totalWithOptions.tmv'))
        .catch(() => null);
    },
  },
  /**
   * Returns TDS2 photos for different vehicle colors
   * Example: https://qa-11-www.edmunds.com/gateway/api/media/v2/toyota/rav4/2022/photos/groupby?submodel=RAV4%20SUV&pageNum=1&pageSize=50&width=600&groupfield=color&shottype=TDS2&sortby=display_order%3Aasc%2Cid%3Aasc%2Cphotoprovider%3Aasc
   * @see buildColorPhotosPath
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].submodels["{submodelName}"].years["{year}"].colorPhotos',
    async resolve({ makeSlug, modelSlug, submodelName, year }, context) {
      return withMetrics(EdmundsAPI, context)
        .fetchJson(
          formatPhotosURL({
            make: makeSlug,
            model: modelSlug,
            year,
            params: {
              submodel: submodelName,
              pageNum: 1,
              pageSize: 50,
              width: 600,
              groupfield: 'color',
              shottype: 'TDS2',
            },
            groupBy: true,
          })
        )
        .then(result =>
          uniqBy(
            get(result, 'photos', []).map(({ title, color, sources }) => ({
              title,
              color: color.split('/')[0],
              src: get(sources, '[0].link.href'),
            })),
            'color'
          )
        )
        .catch(() => []);
    },
  },
]);
